"use client";

import { useMemo } from "react";
import Link from "next/link";
import { BookOpen, Lock } from "lucide-react";
import DashboardCard from "./DashboardCard";
import DashboardCardLoading from "./DashboardCardLoading";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { PASS_FEATURES } from "@/types/pass";
import type { ProblemHistory } from "@/lib/types";

type RecentNote = { problemId: string; content: string; ts: number };

/** 한 문제 이력에서 가장 최근에 작성/수정된 오답노트(다중 + 레거시)를 뽑는다. */
function latestNote(h: ProblemHistory): RecentNote | null {
  let ts = 0;
  let content = "";
  for (const n of h.error_notes ?? []) {
    const t = n.updatedAt || n.createdAt || 0;
    if (t >= ts && n.content?.trim()) {
      ts = t;
      content = n.content;
    }
  }
  if ((h.error_note_updated_at ?? 0) > ts && h.error_note?.trim()) {
    ts = h.error_note_updated_at ?? 0;
    content = h.error_note;
  }
  return ts > 0 ? { problemId: h.problemId, content, ts } : null;
}

/** HTML 태그를 제거해 미리보기용 평문으로. */
function toPreview(html: string): string {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * 대시보드 "최근 오답노트" 위젯 (JCG-2073).
 * 풀이 이력(페이지에서 1회 fetch → prop) 중 가장 최근에 작성한 오답노트 1개를 보여준다.
 */
export default function DashboardRecentErrorNote({
  histories,
  loading = false,
  note: injectedNote,
}: {
  histories?: ProblemHistory[];
  loading?: boolean;
  /** 지난 회차 스냅샷(JCG-3283)이 미리 계산한 최근 오답노트 주입값(null이면 "없음"). */
  note?: RecentNote | null;
}) {
  const { isLoaded, hasAccess } = useEffectivePass();
  const canReadNotes = hasAccess(PASS_FEATURES.errorNotes);
  const item = useMemo(() => {
    if (injectedNote !== undefined) return injectedNote;
    // 단일 최댓값만 필요하므로 정렬 대신 1패스 — 중간 배열 할당·O(n log n) 제거(코드리뷰 #8).
    let best: RecentNote | null = null;
    for (const h of histories ?? []) {
      const n = latestNote(h);
      if (n && (!best || n.ts > best.ts)) best = n;
    }
    return best;
  }, [histories, injectedNote]);

  return (
    <DashboardCard icon={BookOpen} title="최근 오답노트" href={isLoaded && canReadNotes ? "/my/error-notes" : undefined}>
      {!isLoaded ? (
        <DashboardCardLoading height={70} label="이용권 확인 중…" />
      ) : !canReadNotes ? (
        <div className="flex flex-col items-center gap-2 py-2 text-center">
          <Lock className="h-5 w-5 text-amber-500" aria-hidden />
          <p className="text-xs text-muted-foreground">
            오답노트는 Premium 이상 이용권에서 볼 수 있어요.
          </p>
          <Link href="/membership" className="rounded-md text-xs font-semibold text-amber-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
            이용권 업그레이드
          </Link>
        </div>
      ) : loading ? (
        <DashboardCardLoading height={70} label="오답노트 불러오는 중…" />
      ) : item ? (
        <Link
          href="/my/error-notes"
          className="block rounded-lg border border-border bg-background px-3 py-2 transition-colors hover:border-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:border-amber-700"
        >
          <span className="block truncate font-mono text-[12px] font-semibold text-foreground">
            {item.problemId}
          </span>
          <span className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground">
            {toPreview(item.content) || "오답노트"}
          </span>
        </Link>
      ) : (
        <p className="py-6 text-center text-[13px] text-muted-foreground">
          아직 작성한 오답노트가 없어요.
        </p>
      )}
    </DashboardCard>
  );
}
